import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getApiUrl } from "../utils/api";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  FaCheck,
  FaPen,
  FaPenNib,
  FaRedo,
  FaThumbtack,
  FaTimes,
  FaUserMinus,
  FaUsers,
} from "react-icons/fa";
import BadgeIcon from "../components/BadgeIcon";
import EmptyState from "../components/EmptyState";
import GroupInviteSection from "../components/GroupInviteSection";
import EditGroupModal from "../components/EditGroupModal";
import GroupDiscussions from "../components/GroupDiscussions";
import GroupRightSidebar from "../components/GroupRightSidebar";
import PoemCard from "../components/PoemCard";
import { UserContext } from "../context/UserContext";
import { useToast } from "../context/ToastContext";
import {
  getAvatarUrl,
  getPoemImageUrl,
  DEFAULT_AVATAR_DATA_URL,
  FALLBACK_AVATAR_SVG,
} from "../utils/avatar";
import { getGroupCoverUrl, onGroupCoverError } from "../utils/cover";
import { useFeedSidebarAlign } from "../hooks/useFeedSidebarAlign";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import "./GroupDetail.css";

const TABS = [
  { id: "poems", label: "Shared Poems" },
  { id: "discussions", label: "Discussions" },
  { id: "about", label: "About" },
  { id: "members", label: "Members" },
  { id: "media", label: "Media" },
];

function avatarOnError(e) {
  e.currentTarget.src =
    e.currentTarget.src === DEFAULT_AVATAR_DATA_URL
      ? FALLBACK_AVATAR_SVG
      : DEFAULT_AVATAR_DATA_URL;
}

export default function GroupDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useContext(UserContext);
  const toast = useToast();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const tabFromUrl = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(() =>
    TABS.some((t) => t.id === tabFromUrl) ? tabFromUrl : "poems"
  );
  const highlightDiscussionId = searchParams.get("discussionId");
  const [joining, setJoining] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [responding, setResponding] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [respondingRequestId, setRespondingRequestId] = useState(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [coverVersion, setCoverVersion] = useState(0);
  const [coverLoadFailed, setCoverLoadFailed] = useState(false);
  const [sharedPoems, setSharedPoems] = useState([]);
  const [poemsLoading, setPoemsLoading] = useState(false);
  const [poemsLoadingMore, setPoemsLoadingMore] = useState(false);
  const [poemsPagination, setPoemsPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersLoadingMore, setMembersLoadingMore] = useState(false);
  const [membersPagination, setMembersPagination] = useState({
    currentPage: 1,
    hasNextPage: false,
  });
  const poemsObserverRef = useRef(null);
  const handleDiscussionCountChange = useCallback((count) => {
    setGroup((prev) => (prev ? { ...prev, discussionCount: count } : prev));
  }, []);
  const layoutRef = useFeedSidebarAlign([loading, group?._id, error, activeTab]);

  const loadSharedPoems = useCallback(
    async (page = 1, append = false) => {
      if (!id) return;
      try {
        if (page === 1) {
          setPoemsLoading(true);
        } else {
          setPoemsLoadingMore(true);
        }

        const res = await fetch(
          getApiUrl(`/api/groups/${id}/poems?page=${page}&limit=10`),
          { credentials: "include" }
        );
        if (res.status === 403 || res.status === 404) {
          if (!append) setSharedPoems([]);
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        const poems = data.poems || [];

        if (append) {
          setSharedPoems((prev) => [...prev, ...poems]);
        } else {
          setSharedPoems(poems);
        }

        setPoemsPagination(
          data.pagination || {
            currentPage: page,
            totalPages: 1,
            hasNextPage: false,
            hasPrevPage: false,
          }
        );
      } catch (err) {
        console.error("Failed to load group poems:", err);
        if (!append) setSharedPoems([]);
      } finally {
        setPoemsLoading(false);
        setPoemsLoadingMore(false);
      }
    },
    [id]
  );

  const loadMoreSharedPoems = useCallback(() => {
    if (poemsLoadingMore || poemsLoading || !poemsPagination.hasNextPage) return;
    loadSharedPoems(poemsPagination.currentPage + 1, true);
  }, [
    poemsLoadingMore,
    poemsLoading,
    poemsPagination.hasNextPage,
    poemsPagination.currentPage,
    loadSharedPoems,
  ]);

  const loadMembers = useCallback(
    async (page = 1, append = false) => {
      if (!id) return;
      try {
        if (append) setMembersLoadingMore(true);
        else setMembersLoading(true);
        const res = await fetch(
          getApiUrl(`/api/groups/${id}/members?page=${page}&limit=24`),
          { credentials: "include" }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const list = Array.isArray(data.members) ? data.members : [];
        setMembers((prev) => (append ? [...prev, ...list] : list));
        setMembersPagination({
          currentPage: data.pagination?.currentPage ?? page,
          hasNextPage: Boolean(data.pagination?.hasNextPage),
        });
      } catch (err) {
        console.error("Failed to load members:", err);
        if (!append) setMembers([]);
      } finally {
        if (append) setMembersLoadingMore(false);
        else setMembersLoading(false);
      }
    },
    [id]
  );

  const loadGroup = async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
        setError("");
      }
      const res = await fetch(getApiUrl(`/api/groups/${id}`), {
        credentials: "include",
      });
      if (res.status === 404) {
        navigate("/404", { replace: true });
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setGroup(data);
    } catch {
      if (!silent) setError("Failed to load group.");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    setGroup(null);
    setSharedPoems([]);
    setMembers([]);
    setPoemsPagination({
      currentPage: 1,
      totalPages: 1,
      hasNextPage: false,
      hasPrevPage: false,
    });
    setMembersPagination({ currentPage: 1, hasNextPage: false });
    loadGroup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (activeTab === "members" && group?._id) {
      loadMembers(1, false);
    }
  }, [activeTab, group?._id, loadMembers]);

  useEffect(() => {
    if (!group?.canViewContent || String(group._id) !== String(id)) {
      return;
    }
    loadSharedPoems(1, false);
  }, [group?.canViewContent, group?._id, id, loadSharedPoems]);

  useEffect(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 200);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const onInvitesChanged = (e) => {
      if (String(e.detail?.groupId) === String(id)) loadGroup({ silent: true });
    };
    const onMembershipChanged = (e) => {
      if (String(e.detail?.groupId) === String(id)) loadGroup({ silent: true });
    };
    window.addEventListener("group-invites-changed", onInvitesChanged);
    window.addEventListener("group-membership-changed", onMembershipChanged);
    return () => {
      window.removeEventListener("group-invites-changed", onInvitesChanged);
      window.removeEventListener("group-membership-changed", onMembershipChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!user || !group?.isMember) return;
    window.dispatchEvent(
      new CustomEvent("groups-updated", {
        detail: { groupId: id, newPostCount: 0, skipFetch: true },
      })
    );
    fetch(getApiUrl(`/api/groups/${id}/seen`), {
      method: "POST",
      credentials: "include",
    }).catch(() => {});
  }, [user, group?.isMember, id]);

  useEffect(() => {
    const onGroupNewPost = (e) => {
      if (String(e.detail?.groupId) === String(id)) {
        loadGroup({ silent: true });
        loadSharedPoems(1, false);
      }
    };
    window.addEventListener("group-new-post", onGroupNewPost);
    return () => window.removeEventListener("group-new-post", onGroupNewPost);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, loadSharedPoems]);

  useInfiniteScroll({
    enabled: activeTab === "poems" && Boolean(group?.canViewContent),
    hasNextPage: poemsPagination.hasNextPage,
    isLoading: poemsLoading,
    isLoadingMore: poemsLoadingMore,
    onLoadMore: loadMoreSharedPoems,
    sentinelRef: poemsObserverRef,
  });

  useEffect(() => {
    const onSetTab = (e) => {
      if (String(e.detail?.groupId) !== String(id)) return;
      const tab = e.detail?.tab;
      if (TABS.some((t) => t.id === tab)) setActiveTab(tab);
    };
    window.addEventListener("group-set-tab", onSetTab);
    return () => window.removeEventListener("group-set-tab", onSetTab);
  }, [id]);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && TABS.some((t) => t.id === tab)) setActiveTab(tab);
  }, [searchParams]);

  useEffect(() => {
    if (!highlightDiscussionId || loading || !group) return;
    const timer = setTimeout(() => {
      const params = new URLSearchParams(searchParams);
      if (!params.has("discussionId")) return;
      params.delete("discussionId");
      navigate(
        { pathname: `/groups/${id}`, search: params.toString() },
        { replace: true }
      );
    }, 3500);
    return () => clearTimeout(timer);
  }, [highlightDiscussionId, loading, group, id, navigate, searchParams]);

  const mediaItems = useMemo(() => {
    if (!sharedPoems.length) return [];
    return sharedPoems
      .filter((entry) => entry.poem?.coverImage)
      .map((entry) => ({
        id: entry.poem._id,
        url: getPoemImageUrl(entry.poem.coverImage),
        title: entry.poem.title,
      }));
  }, [sharedPoems]);

  const isOpenGroup = group?.joinPolicy === "open";

  const handleJoin = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    setJoining(true);
    setError("");
    try {
      const res = await fetch(getApiUrl(`/api/groups/${id}/join`), {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to join");
      await loadGroup();
      window.dispatchEvent(new CustomEvent("groups-updated"));
      toast.success(
        data.joined ? "You joined the group!" : "Join request sent to the creator"
      );
    } catch (err) {
      setError(err.message || "Failed to join group.");
    } finally {
      setJoining(false);
    }
  };

  const handleJoinRequestResponse = async (userId, accept) => {
    setRespondingRequestId(userId);
    setError("");
    try {
      const res = await fetch(
        getApiUrl(`/api/groups/${id}/join-requests/respond`),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ userId, accept }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to respond");
      await loadGroup();
      window.dispatchEvent(new CustomEvent("groups-updated"));
    } catch (err) {
      setError(err.message || "Failed to respond to join request.");
    } finally {
      setRespondingRequestId(null);
    }
  };

  const handleInviteResponse = async (accept) => {
    if (!user) {
      navigate("/login");
      return;
    }
    setResponding(true);
    setError("");
    try {
      const res = await fetch(getApiUrl(`/api/groups/${id}/invites/respond`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ accept }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to respond");
      await loadGroup();
    } catch (err) {
      setError(err.message || "Failed to respond to invite.");
    } finally {
      setResponding(false);
    }
  };

  const handleRemoveMember = async (userId, { isSelf = false } = {}) => {
    const confirmed = window.confirm(
      isSelf
        ? "Leave this group? You can request to join again later."
        : "Remove this member from the group?"
    );
    if (!confirmed) return;

    setRemovingId(userId);
    setError("");
    try {
      const res = await fetch(
        getApiUrl(`/api/groups/${id}/members/${userId}`),
        { method: "DELETE", credentials: "include" }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to remove member");
      window.dispatchEvent(new CustomEvent("groups-updated"));
      if (isSelf) {
        navigate("/");
        return;
      }
      await loadGroup({ silent: true });
      await loadMembers(1, false);
    } catch (err) {
      setError(err.message || "Failed to remove member.");
    } finally {
      setRemovingId(null);
    }
  };

  const creatorId = group?.creator?._id || group?.createdBy?._id || group?.createdBy;
  const isCreator =
    user && creatorId && String(user._id) === String(creatorId);

  const handleEditSaved = async ({ coverChanged } = {}) => {
    if (coverChanged) setCoverVersion((v) => v + 1);
    await loadGroup({ silent: true });
    window.dispatchEvent(new CustomEvent("groups-updated"));
    toast.success("Group updated");
  };

  const coverUrl = group?.coverImage ? getPoemImageUrl(group.coverImage) : null;
  const defaultCoverSrc = getGroupCoverUrl(null);
  const coverSrc =
    coverLoadFailed || !coverUrl
      ? defaultCoverSrc
      : `${coverUrl}${coverUrl.includes("?") ? "&" : "?"}v=${coverVersion}`;

  useEffect(() => {
    setCoverLoadFailed(false);
  }, [group?.coverImage, coverVersion]);
  const createdLabel = group?.createdAt
    ? new Date(group.createdAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;

  const renderMembersPanel = () => (
    <div className="group-page__panel-body">
      {isCreator && group.pendingJoinRequests?.length > 0 && (
        <>
          <p className="group-page__panel-title mb-2">Join requests</p>
          <ul className="group-page__invite-list mb-4">
            {group.pendingJoinRequests.map((request) => (
              <li
                key={request.user?._id || request.user}
                className="group-page__invite-row"
              >
                <img
                  src={getAvatarUrl(request.user?.avatar)}
                  alt=""
                  className="group-page__invite-avatar"
                  onError={avatarOnError}
                />
                <div className="group-page__invite-info">
                  <Link
                    to={`/users/${request.user?.username || ""}`}
                    className="group-page__invite-name"
                  >
                    {request.user?.username || "User"}
                  </Link>
                  <span className="group-page__invite-status">Wants to join</span>
                </div>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() =>
                      handleJoinRequestResponse(request.user._id, true)
                    }
                    disabled={respondingRequestId === request.user._id}
                    className="group-page__btn group-page__btn--primary group-page__btn--sm"
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      handleJoinRequestResponse(request.user._id, false)
                    }
                    disabled={respondingRequestId === request.user._id}
                    className="group-page__btn group-page__btn--secondary group-page__btn--sm"
                  >
                    Decline
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
      {group.pendingInvites?.length > 0 && (
        <>
          <p className="group-page__panel-title mb-2">Pending invites</p>
          <ul className="group-page__invite-list mb-4">
            {group.pendingInvites.map((invite) => (
              <li
                key={invite.user?._id || invite.user}
                className="group-page__invite-row"
              >
                <img
                  src={getAvatarUrl(invite.user?.avatar)}
                  alt=""
                  className="group-page__invite-avatar"
                  onError={avatarOnError}
                />
                <div className="group-page__invite-info">
                  <Link
                    to={`/users/${invite.user?.username || ""}`}
                    className="group-page__invite-name"
                  >
                    {invite.user?.username || "User"}
                  </Link>
                  <span className="group-page__invite-status">Awaiting response</span>
                </div>
                <span className="group-page__pending-badge">Sent</span>
              </li>
            ))}
          </ul>
        </>
      )}
      {!membersLoading && members.length === 0 ? (
        <EmptyState
          icon="user"
          title="No members yet"
          message="Be the first to join this group."
        />
      ) : membersLoading && members.length === 0 ? (
        <p className="text-sm text-[#777777]">Loading members...</p>
      ) : (
        <>
        <ul className="group-page__member-grid">
          {members.map((member) => {
            const memberIsCreator =
              creatorId && String(member._id) === String(creatorId);
            const canRemove =
              isCreator &&
              String(member._id) !== String(user?._id) &&
              !memberIsCreator;
            return (
              <li key={member._id} className="group-page__member-item">
                <Link
                  to={`/users/${member.username}`}
                  className="group-page__member-card"
                >
                  <img
                    src={getAvatarUrl(member.avatar)}
                    alt=""
                    className="group-page__member-avatar"
                    onError={avatarOnError}
                  />
                  <span className="group-page__member-name">
                    {member.username}
                    <BadgeIcon type={member.badge} className="text-[#f26522]" />
                  </span>
                  {member.name && (
                    <span className="group-page__member-sub">{member.name}</span>
                  )}
                  {memberIsCreator && (
                    <span className="group-page__member-role">Creator</span>
                  )}
                </Link>
                {canRemove && (
                  <button
                    type="button"
                    onClick={() => handleRemoveMember(member._id)}
                    disabled={removingId === member._id}
                    className="group-page__member-remove"
                    title={`Remove ${member.username}`}
                  >
                    <FaUserMinus className="w-3 h-3" aria-hidden="true" />
                    {removingId === member._id ? "..." : "Remove"}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
        {membersPagination.hasNextPage && (
          <button
            type="button"
            className="peer-home-btn-secondary mt-4"
            disabled={membersLoadingMore}
            onClick={() =>
              loadMembers((membersPagination.currentPage || 1) + 1, true)
            }
          >
            {membersLoadingMore ? "Loading..." : "Load more members"}
          </button>
        )}
        </>
      )}
    </div>
  );

  if (loading) {
    return (
      <div ref={layoutRef} className="group-page group-page--panes">
        <div className="group-page__layout">
          <div className="group-page__main">
        <div className="group-page__wrap group-page__skeleton">
          <div className="group-page__hero group-page__hero--default">
            <div className="group-page__hero-inner">
              <div className="group-page__skeleton-block h-9 w-64 mb-3 opacity-30" />
              <div className="group-page__skeleton-block h-4 w-full max-w-md opacity-30" />
            </div>
          </div>
        </div>
          </div>
          <aside className="group-page__rail">
            <GroupRightSidebar />
          </aside>
        </div>
      </div>
    );
  }

  if (error && !group) {
    return (
      <div ref={layoutRef} className="group-page group-page--panes">
        <div className="group-page__layout">
          <div className="group-page__main">
        <div className="group-page__wrap">
          <div className="group-page__panel">
            <div className="group-page__empty">
              <p className="text-red-700 mb-4">{error}</p>
              <button
                type="button"
                onClick={loadGroup}
                className="group-page__btn group-page__btn--primary"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
          </div>
          <aside className="group-page__rail">
            <GroupRightSidebar />
          </aside>
        </div>
      </div>
    );
  }

  if (!group) return null;

  return (
    <div ref={layoutRef} className="group-page group-page--panes">
      <div className="group-page__layout">
        <div className="group-page__main">
      <div className="group-page__wrap">
        <Helmet>
          <title>{`${group.name} – PeerPoetry`}</title>
        </Helmet>

        <header
          className={`group-page__hero${
            coverUrl && !coverLoadFailed
              ? " group-page__hero--image"
              : " group-page__hero--default"
          }`}
        >
          <img
            key={coverSrc}
            src={coverSrc}
            alt=""
            className="group-page__hero-cover"
            onError={(e) => {
              onGroupCoverError(e);
              setCoverLoadFailed(true);
            }}
          />
          <div className="group-page__hero-overlay" aria-hidden="true" />
          <div className="group-page__hero-inner">
            <p className="group-page__breadcrumb group-page__breadcrumb--light">
              <Link to="/">HOME</Link>
              <span aria-hidden="true"> / </span>
              <span>GROUP</span>
            </p>

            <div className="group-page__title-row">
              <div>
                <h1 className="group-page__title group-page__title--hero">
                  {group.name}
                </h1>
                {group.tagline && (
                  <p className="group-page__tagline">{group.tagline}</p>
                )}
              </div>

              <div className="group-page__actions">
                {user && group.hasPendingInvite && !group.isMember && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleInviteResponse(true)}
                      disabled={responding}
                      className="group-page__btn group-page__btn--primary"
                    >
                      {responding ? "..." : "Accept invite"}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleInviteResponse(false)}
                      disabled={responding}
                      className="group-page__btn group-page__btn--hero-ghost"
                    >
                      Decline
                    </button>
                  </>
                )}
                {user &&
                  !group.isMember &&
                  !group.hasPendingInvite &&
                  group.hasPendingJoinRequest &&
                  !isOpenGroup && (
                    <span className="group-page__pill group-page__pill--pending">
                      Join request pending
                    </span>
                  )}
                {user &&
                  !group.isMember &&
                  !group.hasPendingInvite &&
                  !group.hasPendingJoinRequest &&
                  group.wasRemoved && (
                    <span className="group-page__pill group-page__pill--pending">
                      Removed — invite only
                    </span>
                  )}
                {user &&
                  !group.isMember &&
                  !group.hasPendingInvite &&
                  !group.hasPendingJoinRequest &&
                  !group.wasRemoved && (
                    <button
                      type="button"
                      onClick={handleJoin}
                      disabled={joining}
                      className="group-page__btn group-page__btn--primary"
                    >
                      {joining
                        ? isOpenGroup
                          ? "Joining..."
                          : "Sending..."
                        : isOpenGroup
                        ? "Join group"
                        : "Request to join"}
                    </button>
                  )}
                {!user && (
                  <Link to="/login" className="group-page__btn group-page__btn--primary">
                    {isOpenGroup ? "Log in to join" : "Log in to request access"}
                  </Link>
                )}
                {user && group.isMember && (
                  <>
                    {isCreator && (
                      <button
                        type="button"
                        onClick={() => setShowEditModal(true)}
                        className="group-page__btn group-page__btn--hero-ghost"
                      >
                        <FaPen className="w-3 h-3" aria-hidden="true" />
                        Edit group
                      </button>
                    )}
                    <span
                      className="group-page__btn group-page__btn--hero-ghost group-page__btn--member"
                      aria-label="You are a member of this group"
                    >
                      <FaCheck className="w-3 h-3" aria-hidden="true" />
                      You&apos;re a member
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveMember(user._id, { isSelf: true })}
                      disabled={removingId === user._id}
                      className="group-page__btn group-page__btn--hero-ghost"
                    >
                      {removingId === user._id ? "Leaving..." : "Leave group"}
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="group-page__meta group-page__meta--hero">
              <span className="group-page__pill group-page__pill--hero">
                {isOpenGroup ? "Open group" : "Approval required"}
              </span>
              <span className="group-page__pill group-page__pill--hero">
                <FaUsers className="w-3 h-3" aria-hidden="true" />
                {group.memberCount} member{group.memberCount === 1 ? "" : "s"}
              </span>
              {group.creator?.username && (
                <span className="group-page__pill group-page__pill--hero">
                  Created by{" "}
                  <Link
                    to={`/users/${group.creator.username}`}
                    className="group-page__pill-link group-page__pill-link--light"
                  >
                    {group.creator.username}
                  </Link>
                  {createdLabel && ` · ${createdLabel}`}
                </span>
              )}
            </div>

            {error && (
              <p className="group-page__error group-page__error--hero">{error}</p>
            )}
          </div>
        </header>

        <div className="group-page__content">
        {!group.isMember ? (
          <section className="group-page__panel group-page__locked mb-6">
            <div className="group-page__panel-body">
              <EmptyState
                icon="user"
                title="Members only"
                message={
                  isOpenGroup
                    ? "Join this open group to see shared poems, discussions, and members."
                    : "Request to join this group to see shared poems and members."
                }
              />
            </div>
          </section>
        ) : (
          <>
            <nav className="group-page__tabs" aria-label="Group sections">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`group-page__tab${
                    activeTab === tab.id ? " group-page__tab--active" : ""
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </nav>

            {activeTab === "poems" && (
              <>
                <div className="group-page__composer">
                  {user && (
                    <img
                      src={getAvatarUrl(user.avatar)}
                      alt=""
                      className="group-page__composer-avatar"
                      onError={avatarOnError}
                    />
                  )}
                  <Link
                    to={`/groups/${id}/write`}
                    className="group-page__composer-input"
                  >
                    Share a poem with {group.name}…
                  </Link>
                  <div className="group-page__composer-actions">
                    <Link
                      to={`/groups/${id}/write`}
                      className="group-page__composer-action"
                    >
                      <FaPen aria-hidden="true" />
                      Poem
                    </Link>
                  </div>
                </div>

                <section
                  className="group-page__feed-section"
                  aria-labelledby="group-shared-poems-title"
                >
                  <h2 id="group-shared-poems-title" className="group-page__feed-title">
                    Latest shared poems
                  </h2>
                  {poemsLoading ? (
                    <p className="group-page__feed-loading">Loading poems…</p>
                  ) : sharedPoems.length === 0 ? (
                    <EmptyState
                      icon="poem"
                      title="No poems shared yet"
                      message="Write a poem for this group using the composer above."
                    />
                  ) : (
                    <div className="group-page__feed-list">
                      {sharedPoems.map((entry) => (
                        <article
                          key={entry._id || `${entry.poem?._id}-${entry.sharedAt}`}
                          className="group-page__feed-card"
                        >
                          <div className="group-page__feed-card-head">
                            <div className="group-page__feed-card-meta">
                              {entry.sharedBy?.username && (
                                <>
                                  <img
                                    src={getAvatarUrl(entry.sharedBy.avatar)}
                                    alt=""
                                    className="group-page__feed-avatar"
                                    onError={avatarOnError}
                                  />
                                  <Link
                                    to={`/users/${entry.sharedBy.username}`}
                                    className="group-page__feed-author"
                                  >
                                    {entry.sharedBy.username}
                                  </Link>
                                </>
                              )}
                              {entry.sharedAt && (
                                <time
                                  dateTime={entry.sharedAt}
                                  className="group-page__feed-date"
                                >
                                  {new Date(entry.sharedAt).toLocaleDateString(
                                    "en-US",
                                    {
                                      month: "short",
                                      day: "numeric",
                                      year: "numeric",
                                    }
                                  )}
                                </time>
                              )}
                            </div>
                            {entry.pinned && (
                              <span className="group-page__pinned">
                                <FaThumbtack aria-hidden="true" />
                                Pinned
                              </span>
                            )}
                          </div>
                          <PoemCard poem={entry.poem} showSave={false} />
                        </article>
                      ))}
                      {poemsLoadingMore && (
                        <div className="group-page__feed-loading group-page__feed-loading--more">
                          <div className="group-page__feed-spinner" />
                          <p>Loading more poems…</p>
                        </div>
                      )}
                      {poemsPagination.hasNextPage && !poemsLoadingMore && (
                        <div ref={poemsObserverRef} className="group-page__load-more-wrap">
                          <button
                            type="button"
                            onClick={loadMoreSharedPoems}
                            className="group-page__load-more"
                          >
                            <FaRedo aria-hidden="true" />
                            Load more
                          </button>
                        </div>
                      )}
                      {!poemsPagination.hasNextPage && sharedPoems.length > 0 && (
                        <p className="group-page__feed-end">You&apos;ve reached the end</p>
                      )}
                    </div>
                  )}
                </section>
              </>
            )}

            {activeTab === "about" && (
              <section className="group-page__panel mb-6">
                <div className="group-page__panel-head">
                  <h2 className="group-page__feed-title">About this group</h2>
                  {isCreator && (
                    <button
                      type="button"
                      onClick={() => setShowEditModal(true)}
                      className="group-page__edit-btn"
                    >
                      <FaPen className="w-3 h-3" aria-hidden="true" />
                      Edit group
                    </button>
                  )}
                </div>
                <div className="group-page__panel-body group-page__about">
                  {group.tagline && (
                    <p className="group-page__about-tagline">{group.tagline}</p>
                  )}
                  <p className="group-page__about-text">
                    {group.description ||
                      "This group has not added a description yet."}
                  </p>
                  <dl className="group-page__about-stats">
                    <div>
                      <dt>Join policy</dt>
                      <dd>{isOpenGroup ? "Open — anyone can join" : "Approval required"}</dd>
                    </div>
                    <div>
                      <dt>Members</dt>
                      <dd>{group.memberCount}</dd>
                    </div>
                    <div>
                      <dt>Poems</dt>
                      <dd>{group.poemCount ?? 0}</dd>
                    </div>
                    <div>
                      <dt>Discussions</dt>
                      <dd>{group.discussionCount ?? 0}</dd>
                    </div>
                    <div>
                      <dt>Created</dt>
                      <dd>{createdLabel || "—"}</dd>
                    </div>
                  </dl>
                </div>
              </section>
            )}

            {activeTab === "members" && (
              <section className="group-page__panel mb-6" aria-labelledby="group-members-title">
                <div className="group-page__panel-head">
                  <h2 id="group-members-title" className="group-page__feed-title">
                    Members
                  </h2>
                  <span className="group-page__panel-count">{group.memberCount}</span>
                </div>
                <div className="group-page__invite-panel">
                  <GroupInviteSection
                    groupId={id}
                    groupName={group.name}
                    instanceId="panel"
                  />
                </div>
                {renderMembersPanel()}
              </section>
            )}

            {activeTab === "discussions" && (
              <section className="group-page__panel mb-6" aria-labelledby="group-discussions-title">
                <div className="group-page__panel-head">
                  <h2 id="group-discussions-title" className="group-page__feed-title">
                    Discussions
                  </h2>
                  <span className="group-page__panel-count">
                    {group.discussionCount ?? 0}
                  </span>
                </div>
                <div className="group-page__panel-body">
                  <GroupDiscussions
                    groupId={id}
                    groupName={group.name}
                    isCreator={isCreator}
                    discussionCount={group.discussionCount ?? 0}
                    highlightDiscussionId={highlightDiscussionId}
                    onCountChange={handleDiscussionCountChange}
                  />
                </div>
              </section>
            )}

            {activeTab === "media" && (
              <section className="group-page__panel mb-6">
                <div className="group-page__panel-head">
                  <h2 className="group-page__feed-title">Media</h2>
                </div>
                <div className="group-page__panel-body">
                  {mediaItems.length === 0 ? (
                    <EmptyState
                      icon="poem"
                      title="No media yet"
                      message="Cover images from shared poems will appear here."
                    />
                  ) : (
                    <ul className="group-page__media-grid">
                      {mediaItems.map((item) => (
                        <li key={item.id}>
                          <Link
                            to={`/poems/${item.id}`}
                            className="group-page__media-item"
                            title={item.title}
                          >
                            <img src={item.url} alt="" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>
            )}
          </>
        )}
        </div>
      </div>
        </div>
        <aside className="group-page__rail">
          <GroupRightSidebar />
        </aside>
      </div>
      {showBackToTop && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="group-page__back-to-top"
          aria-label="Back to top"
        >
          ↑ Back to top
        </button>
      )}
      <EditGroupModal
        open={showEditModal}
        groupId={id}
        initialName={group.name}
        initialTagline={group.tagline || ""}
        initialDescription={group.description || ""}
        initialJoinPolicy={group.joinPolicy || "approval"}
        initialCoverImage={group.coverImage || ""}
        coverVersion={coverVersion}
        onClose={() => setShowEditModal(false)}
        onSaved={handleEditSaved}
      />
    </div>
  );
}
